import { create } from 'zustand';
import { createJSONStorage, persist } from "zustand/middleware";
import { SegSelectOption } from '../../Components/SegUI/SegSelect/SegSelect';
import { CotizacionDesc } from '../../Pages/Auto/Precios';
import { CotizacionDescPrepaga } from '../../Pages/Prepaga/Individuo/Precios';

export interface IFechaNacimiento {
    dia: string;
    mes: string;
    anio: string;
}

export interface ITelefono {
    cod: string;
    numero: string;
}

export interface IVencimiento {
    mes: string;
    anio: string;
}

export interface CertificadoNoRodamiento {
    archivo: File;
    extension: string;
    nombre: string;
    dataBase64: string;
}


/** Un cilindro de GNC declarado en la pantalla del vehículo. */
export interface ICilindroGnc {
    marca?: string;
    numero?: string;
}

/**
 * Detalle de GNC (camino BFF). El checkbox "¿Tiene GNC?" sigue viviendo en la
 * revisión de datos; esto es lo que se carga después, en la pantalla del
 * vehículo, y que el monolito pedía en su formulario de emisión.
 */
export interface IGncDetalle {
    numeroOblea?: string;
    /** ISO yyyy-MM-dd. */
    vencimientoOblea?: string;
    marcaRegulador?: string;
    numeroRegulador?: string;
    cilindros?: ICilindroGnc[];
}

/** Domicilio donde se hace la inspección, cuando el tipo es a domicilio. */
export interface IDomicilioInspeccion {
    provinciaId?: string;
    localidad?: string;
    codigoPostal?: string;
    calle?: string;
    altura?: string;
    /** ISO yyyy-MM-dd. */
    fecha?: string;
    horarioDesde?: string;
    horarioHasta?: string;
    codigoArea?: string;
    telefono?: string;
}

/** Coordinación de la inspección del vehículo (camino BFF). */
export interface IInspeccion {
    tipo?: string;
    domicilio?: IDomicilioInspeccion;
    centroCompania?: string;
    ciaCodigoArea?: string;
    ciaTelefono?: string;
}

export interface IEmisionValues {
    id?: number;
    cotizacionDesc?: CotizacionDesc;
    CotizacionDescPrepaga?: CotizacionDescPrepaga;
    cotizacionDescAP?: any;
    tipoPersona?: SegSelectOption;
    condicionIva?: SegSelectOption;
    tipoUso?: SegSelectOption;
    gnc?: boolean;
    /** Detalle de GNC: oblea, regulador y cilindros (camino BFF). */
    gncDetalle?: IGncDetalle;
    /** Tipo de inspección y, si es a domicilio, dónde y cuándo (camino BFF). */
    inspeccion?: IInspeccion;
    nombre?: string;
    apellido?: string;
    email?: string;
    tipoDoc?: string;
    numDoc?: string;
    cuil?: string;
    fechaNac?: IFechaNacimiento;
    celular?: ITelefono;
    telAdicional?: ITelefono;
    localidad?: string;
    calle?: string;
    altura?: string;
    piso?: string;
    departamento?: string;
    patente?: string;
    numMotor?: string;
    numChasis?: string;
    /** Vencimiento de la VTV (YYYY-MM-DD). Solo se pide si el plan la exige (US 24094). */
    vtv?: string;
    numeroTarjeta?: string;
    titularTarjeta?: string;
    banco?: SegSelectOption;
    tipoTarjeta?: SegSelectOption;
    vencimientoTarjeta?: IVencimiento;
    cvc?: string;
    certificadoNoRodamiento?: CertificadoNoRodamiento | null;
    calleSinNum?: boolean;
    accidentesPersonales?: AccidentesPersonales;
    idPoliza?: number;
    /** Id opaco de la póliza emitida por el BFF (camino BFF). */
    polizaId?: string;
    /** Código postal del domicilio de radicación (obligatorio en el camino BFF). */
    codigoPostal?: string;
    /** Idempotency-Key del intento de emisión en curso (camino BFF). */
    idempotencyKey?: string;
    /**
     * La emisión quedó colgada del lado del BFF y la confirma una persona
     * (camino BFF, `requiereAsistencia`). La confirmación avisa y no ofrece
     * reintentar: un segundo intento arriesga un segundo cobro.
     */
    emisionRequiereAsistencia?: boolean;
    /** Estado de los documentos que devolvió la emisión (camino BFF). */
    documentos?: Record<string, string>;
}

interface AccidentesPersonales {
    clausulaNoRepeticion?: boolean;
    asegurados?: AseguradoAP[];
    beneficiario?: {
        nombre: string;
        apellido: string;
        tipoDoc: string;
        numDoc: string;
        fechaNac: IFechaNacimiento;
    }
}

export interface AseguradoAP {
    nombre: string;
    apellido: string;
    profesion: SegSelectOption;
    tipoDoc: string;
    numDoc: string;
    genero: SegSelectOption;
    fechaNac: IFechaNacimiento;
    beneficiario?: {
        nombre: string;
        apellido: string;
        tipoDoc: string;
        numDoc: string;
        fechaNac: IFechaNacimiento;
    }
}
interface EmisionStore {
    emisionValues: IEmisionValues;
    setEmisionValues: (values: IEmisionValues) => void;
    resetEmisionValues: () => void;

}

export const useEmisionStore = create<EmisionStore>()(
    persist(
        (set) => ({
            emisionValues: {},
            setEmisionValues: (values: IEmisionValues) => set({ emisionValues: { ...values } }),
            resetEmisionValues: () => set({ emisionValues: {} }),
        })
        , {
            name: "emision",
            storage: createJSONStorage(() => sessionStorage),
        }))